/**
 * `image_text` — side-by-side image and copy with an optional CTA (module 22 D3).
 *
 * Responsive without a media query: the two panes are flex items that wrap to a
 * single column below ~600px. `imageSide` sets DOM order, so on mobile the image
 * stacks above (left) or below (right) the text accordingly. A dangling CTA
 * drops rather than 404s (spec 42 R3).
 */
import type { LinkTarget } from "@flint/api-contract/store-home";
import { resolveTarget, isExternal, type TargetContext } from "./resolve-target.js";
import { SectionImage } from "./section-image.js";

export function ImageText({
  imageUrl,
  heading,
  body,
  imageSide,
  cta,
  ctx,
}: {
  imageUrl?: string | undefined;
  heading: string;
  body?: string | undefined;
  imageSide: "left" | "right";
  cta?: { label: string; target: LinkTarget } | undefined;
  ctx: TargetContext;
}) {
  const href = cta ? resolveTarget(cta.target, ctx) : null;

  const media = imageUrl ? (
    <div style={{ flex: "1 1 280px" }}>
      <SectionImage src={imageUrl} alt="" aspectRatio="4 / 3" radius="var(--r)" />
    </div>
  ) : null;

  const text = (
    <div
      style={{
        flex: "1 1 280px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <h2
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 500,
          fontSize: "1.625rem",
          lineHeight: 1.2,
          color: "var(--ink)",
          margin: 0,
        }}
      >
        {heading}
      </h2>
      {body ? (
        <p
          style={{
            marginTop: "0.75rem",
            fontSize: "0.9375rem",
            lineHeight: 1.6,
            color: "var(--ink-2)",
            whiteSpace: "pre-wrap",
          }}
        >
          {body}
        </p>
      ) : null}
      {cta && href ? (
        <a
          href={href}
          {...(isExternal(cta.target) ? { rel: "noopener noreferrer", target: "_blank" } : {})}
          style={{
            alignSelf: "flex-start",
            marginTop: "1.25rem",
            padding: "0.625rem 1.5rem",
            borderRadius: "var(--r-sm)",
            backgroundColor: "var(--accent)",
            color: "var(--accent-fg)",
            fontWeight: 600,
            fontSize: "0.9375rem",
            textDecoration: "none",
          }}
        >
          {cta.label}
        </a>
      ) : null}
    </div>
  );

  return (
    <section
      aria-label={heading}
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "stretch",
        gap: "clamp(1.5rem, 4vw, 3rem)",
      }}
    >
      {imageSide === "left" ? (
        <>
          {media}
          {text}
        </>
      ) : (
        <>
          {text}
          {media}
        </>
      )}
    </section>
  );
}
